<template>
    <application-layout>
        <v-main>
            <v-container>
                <v-form
                  ref="editLandingPageForm"
                  @submit.prevent="submitLandingPageForm"
                >
                    <h1 class="mb-2">Edit Landing Page</h1>
                    <v-row >
                        <v-col cols="12">
                            <h2 class="mt-4">Visibility</h2>
                            <v-switch
                                data-testid="landing-page-visibility-switch"
                                class="landing-page-visibility-switch"
                                :label='currentLandingPage.published ? "Published" : "Not Published"'
                                v-model="currentLandingPage.published"
                            />
                        </v-col>
                    </v-row>
                    <v-row >
                        <v-col cols="12">
                            <h2>SEO</h2>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <app-input
                                data-testid="landing-page-edit-title-input"
                                class="landing-page-edit-title-input"
                                :type="'text'"
                                label="Page Title"
                                v-model.trim="currentLandingPage.meta_title"
                                :rules="{ required: true }"
                                :useValidation="true"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <app-input
                                data-testid="landing-page-edit-breadcrumb-input"
                                class="landing-page-edit-breadcrumb-input"
                                label="Breadcrumb Label"
                                :type="'text'"
                                v-model.trim="currentLandingPage.meta_breadcrumb_title"
                                :rules="{ required: true }"
                                :useValidation="true"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <app-input
                                data-testid="landing-page-edit-slug-input"
                                class="landing-page-edit-slug-input"
                                :type="'text'"
                                label="Page URL"
                                v-model.trim="currentLandingPage.slug"
                                hint="Leave this blank if you want to use the default structure"
                                :rules="{ required: true }"
                                :useValidation="true"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <app-input
                                data-testid="landing-page-edit-meta-description-input"
                                class="landing-page-edit-meta-description-input"
                                :type="'text'"
                                label="Meta Description"
                                v-model.trim="currentLandingPage.meta_description"
                                :rules="{ required: true }"
                                :useValidation="true"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <app-input
                                data-testid="landing-page-edit-meta-keywords-input"
                                class="landing-page-edit-meta-keywords-input"
                                :type="'text'"
                                label="Meta Keywords"
                                v-model.trim="currentLandingPage.meta_keywords"
                            />
                        </v-col>
                    </v-row>
                    <v-row  class="mb-4">
                        <v-col cols="12">
                            <h2>Content</h2>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <app-input
                                data-testid="landing-page-edit-header-input"
                                class="landing-page-edit-header-input"
                                :type="'text'"
                                label="Page Header"
                                v-model.trim="currentLandingPage.page_header"
                            />
                        </v-col>
                        <v-col cols="12">
                            <froala
                                data-testid="landing-page-edit-froala-textarea"
                                class="landing-page-edit-froala-textarea"
                                :tag="'textarea'"
                                :config="froalaConfig"
                                v-model.trim="currentLandingPage.content"
                                id="content"
                            />
                        </v-col>
                    </v-row>
                    <v-row >
                        <v-col cols="12">
                            <h2>Custom Code</h2>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-textarea
                                data-testid="landing-page-edit-markup-head-textarea"
                                class="landing-page-edit-markup-head-textarea"
                                label="Custom Markup (head)"
                                filled
                                hint="Code in this box will be injected right before the closing head tag on this page"
                                v-model.trim="currentLandingPage.custom_markup_head"
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-textarea
                                data-testid="landing-page-edit-markup-body-textarea"
                                class="landing-page-edit-markup-body-textarea"
                                label="Custom Markup (body)"
                                filled
                                hint="Code in this box will be injected right before the closing body tag on this page"
                                v-model.trim="currentLandingPage.custom_markup_body"
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-textarea
                                data-testid="landing-page-edit-css-textarea"
                                class="landing-page-edit-css-textarea"
                                label="Custom CSS"
                                filled
                                v-model.trim="currentLandingPage.custom_css"
                                clearable
                            />
                        </v-col>
                    </v-row>
                    <v-row >
                        <v-col cols="12">
                            <h2>Facilities Displayed on Page</h2>
                        </v-col>
                        <v-col cols="4">
                            <app-input
                                data-testid="landing-page-edit-menu-title-input"
                                class="landing-page-edit-menu-title-input"
                                :type="'text'"
                                label="Label Above Facilities"
                                v-model.trim="currentLandingPage.meta_menu_title"
                                :rules="{ required: true }"
                                :useValidation="true"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-autocomplete
                                :items="facilities"
                                item-value="id"
                                item-text="location_code"
                                v-model="currentLandingPage.facilities"
                                :menu-props="{ maxHeight: '400' }"
                                label="Facilities"
                                multiple
                                filled
                                chips
                                hint="Select which Store Space facilities to display on this page."
                                persistent-hint
                                clearable
                            >
                                <template
                                    slot="selection"
                                    slot-scope="data"
                                >
                                    <v-chip
                                        data-testid="landing-page-edit-facilities-chip"
                                        class="landing-page-edit-facilities-chip chip--select-multi"
                                        :input-value="data.selected"
                                        close
                                        @click:close="remove(data.item)"
                                    >
                                        {{ data.item.location_code }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                    <v-row >
                        <v-col cols="12">
                            <v-btn
                              data-testid="landing-page-edit-cancel-btn"
                              class="landing-page-edit-cancel-btn error mr-3 ml-0"
                              @click="() => { this.$router.back(); }"
                            >
                              Cancel
                            </v-btn>
                            <v-btn
                                data-testid="landing-page-edit-save-btn"
                                class="landing-page-edit-save-btn"
                                color="primary"
                                type="submit"
                                :loading="loadingState.includes('updating landing page')"
                            >
                                Save
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-container>
        </v-main>
    </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout.vue";

export default {
  data() {
    return {};
  },

  computed: {
    currentLandingPage: {
      get() {
        return this.$store.state.landingPage.currentLandingPage;
      },
      set(newValue) {
        this.$store.commit("setCurrentLandingPageData", newValue);
      }
    },
    facilities() {
      return this.$store.state.facility.facilities;
    },
    loadingState() {
      return this.$store.state.landingPage.loadingState;
    },
    setCustomOrder() {
      if (this.currentLandingPage.facilities) {
        return this.currentLandingPage.facilities.map((facility, index) => {
          return { facility_id: facility, display_order: index };
        }).sort((a, b) => a.display_order - b.display_order);
      } else {
        return [];
      }
    },

    froalaConfig() {
      return {
        ...this.$store.state.app.froalaConfig,
        theme: this.$vuetify.theme.dark ? "dark" : null,
        requestHeaders: {
          Authorization: this.$store.state.auth.access_token
        }
      };
    }
  },

  methods: {
    async submitLandingPageForm() {
      if (this.$refs.editLandingPageForm.validate()) {
        this.currentLandingPage.facilities = this.setCustomOrder;
        await this.$store.dispatch("updateLandingPage");
      }
    },
    remove(item) {
      const index = this.currentLandingPage.facilities.indexOf(item.id);
      this.currentLandingPage.facilities.splice(index, 1);
    }
  },

  components: {
    ApplicationLayout
  },

  created() {
    this.$store.dispatch("getLandingPage", this.$route.params.landingPageId);
    this.$store.dispatch("getAllFacilities", { per_page: 999 });
  }
};
</script>
